import "web-ui/tom-select"
import { Controller } from "@hotwired/stimulus"
import { morphdomPreserveAttributes } from "web-ui/morphdom/preserve-attributes"
import { morphdomIgnore } from "web-ui/morphdom/ignore"
import { resetOptions } from "web-ui/tom-select/reset-options"
import { getCSRFToken } from "web-ui/csrf"
import * as Log from "web-ui/log"

// Connects to data-controller="option-picker-form"
export default class extends Controller {
  static values = {
    inputInstructions: { type: String, default: "Press enter to add" },
    noOptionsMessage: { type: String, default: "No items to add" },
    placeholder: String
  }

  submissionQueue = []
  submitting = false
  retryDelay = 0
  retryTimeout = null

  connect() {
    this.debug("Connect")

    this.connected = true

    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)
    document.addEventListener("turbo:before-cache", this.beforeTurboCache)

    this.initializeTomSelect()
  }

  initializeTomSelect() {
    const select = this.element.querySelector("select")

    this.debug("Initialize TomSelect", {
      id: select.id,
      inputInstructions: this.inputInstructionsValue,
      placeholder: this.placeholderValue
    })

    if (this.tomSelect) {
      this.tomSelect.destroy()
    }

    const plugins = {
      "dropdown_close_on_scroll": {},
      "dropdown_open": {},
      "dropdown_placement": {},
      "dropdown_scroll": {},
      "input_instructions": { instructions: this.inputInstructionsValue },
      "no_options_message": { message: this.noOptionsMessageValue }
    }

    const body = document.querySelector("body")

    this.tomSelect = new TomSelect(select, {
      closeAfterSelect: false,
      dropdownParent: body,
      hideSelected: true,
      onChange: (value) => {
        this.handleSelection(value)
      },
      openOnFocus: true,
      placeholder: this.placeholderValue,
      plugins,
      refreshThrottle: 0
    })

    const controller = this
    this.tomSelect.hook("before", "onOptionSelect", function(event, option) {
      if (option && !option.classList.contains('create')) {
        const next = (this.getAdjacent(option, 1) || this.getAdjacent(option, -1))
        controller.adjacentOptionValue = next ? next.dataset.value : null
      }
    })

    this.tomSelect.control_input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
      }
    })

    morphdomPreserveAttributes(this.element, ["class"])
    morphdomPreserveAttributes(this.tomSelect.input, ["class", "tabindex"])
    morphdomIgnore(this.tomSelect.wrapper, true)
  }

  handleSelection(value) {
    if (!value) {
      return
    }

    const option = this.tomSelect.options[value]
    const text = option.text
    const order = option.$order
    const optgroup = option.optgroup

    const formData = new FormData(this.element)

    const adjacentValue = this.adjacentOptionValue
    this.adjacentOptionValue = null

    this.tomSelect.removeOption(value)
    this.tomSelect.refreshOptions()

    if (adjacentValue) {
      const adjacentOption = this.tomSelect.getOption(adjacentValue)
      if (adjacentOption) {
        this.tomSelect.setActiveOption(adjacentOption)
      }
    }

    this.tomSelect.control_input.select()

    this.submissionQueue.push({ value, text, order, optgroup, formData })

    this.element.classList.add("submitting")
    this.element.setAttribute("aria-busy", true)

    this.processQueue()
  }

  async processQueue() {
    if (this.submitting || this.retryTimeout) {
      return
    }

    const submission = this.submissionQueue.shift()

    if (!submission) {
      this.element.classList.remove("submitting")
      this.element.removeAttribute("aria-busy")

      return
    }

    this.submitting = true

    const { value, text, order, optgroup, formData } = submission

    const url = this.element.action
    const csrfToken = getCSRFToken()

    const params = new URLSearchParams()
    for (const [name, val] of formData) {
      params.append(name, val)
    }

    this.debug("Submit selection", { value })

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken
      },
      body: params
    }

    try {
      const response = await fetch(url, options)

      if (!this.connected) {
        return
      }

      if (response.status >= 500) {
        this.debug("Server error, retrying", { value, status: response.status })
        this.retrySubmission(submission)
        return
      }

      this.debug("Submission complete", { value, status: response.status })
      this.resetRetryDelay()

      const html = await response.text()

      Turbo.renderStreamMessage(html)

    } catch (error) {
      if (!this.connected) {
        return
      }

      this.debug("Network error, retrying", { value, error: error.message })

      Sentry.captureException(error)

      this.retrySubmission(submission)
    }
  }

  retrySubmission(submission) {
    this.retryDelay += 250
    this.retryDelay = Math.min(this.retryDelay, 5000)

    this.submissionQueue.unshift(submission)

    this.submitting = false

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null
      this.processQueue()
    }, this.retryDelay)
  }

  resetRetryDelay() {
    this.retryDelay = 0
  }

  placeholderValueChanged(value) {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  inputInstructionsValueChanged(value) {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  beforeStreamRender = (event) => {
    const stream = event.detail.newStream

    const action = stream.getAttribute("action")
    if (action !== "morph") {
      return
    }

    const target = stream.getAttribute("target")
    const form = this.element
    if (target !== form.id) {
      return
    }

    const render = event.detail.render

    event.detail.render = (self) => {
      render(self)

      const pendingValues = this.submissionQueue.map(({ value }) => value)
      resetOptions(this.tomSelect.input, this.tomSelect, pendingValues)

      this.submitting = false
      this.processQueue()
    }
  }

  beforeTurboCache = () => {
    this.debug("Before Turbo Cache")

    this.tomSelect.destroy()
  }

  disconnect() {
    this.debug("Disconnect")

    this.connected = false

    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)
    document.removeEventListener("turbo:before-cache", this.beforeTurboCache)

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
    }

    this.tomSelect.destroy()
  }

  debug(message, data={}) {
    if (this.tomSelect) {
      Object.assign(data, {
        id: this.tomSelect.input.id
      })
    }

    Log.debug("Option Picker Form - " + message, data)
  }
}
